import axios, { type InternalAxiosRequestConfig } from 'axios'
import { queryClient } from '@/lib/query/queryClient'
import { permissionQueryKeys } from '@/services/permissions/permission.query-keys'
import axiosRetry from 'axios-retry'
import { apiPath } from '@/constants/api-paths'
import { ENV } from '@/config'
import { POC_DRAFT_PROGRESS_KEY } from '@/constants/session.constants'
import { clearLocalStorageExcept, saveToLocalStorage } from '@/utils/localStorage'
import cookieService from '@/utils/cookies'

const authCookieOptions = () => ({
  path: '/',
  expires: new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString(),
  secure: globalThis.location?.protocol === 'https:',
  sameSite: 'Lax' as const,
})

const writeAuthTokensToCookies = (tokenData: {
  access_token?: string
  refresh_token?: string
  expires_in?: number
  token_type?: string
}) => {
  const opts = authCookieOptions()
  if (tokenData.access_token) cookieService.setValue('access_token', tokenData.access_token, opts)
  if (tokenData.refresh_token)
    cookieService.setValue('refresh_token', tokenData.refresh_token, opts)
  if (tokenData.expires_in !== undefined)
    cookieService.setValue('expires_in', String(tokenData.expires_in), opts)
  if (tokenData.token_type) cookieService.setValue('token_type', tokenData.token_type, opts)
}

const BASE_URL = ENV.API.BASE_URL
const SKIP_URLS = [
  apiPath.LOGIN,
  apiPath.REFRESH_TOKEN,
  apiPath.FORGOT_PASSWORD,
  apiPath.FORGOT_PASSWORD_INVITE,
  apiPath.VERIFY_OTP,
  apiPath.SET_RESET_PASSWORD,
  apiPath.LAB_ORDERS_OAUTH_AUTHORIZE,
]

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: ENV.API.TIMEOUT,
  headers: { 'Content-Type': 'application/json' }, // 'ngrok-skip-browser-warning': 'true'
})

axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  // Only retry on actual network errors (no response received).
  // Skip 5xx retries — server errors won't resolve on retry, and retrying
  // PUT/POST mutations can double-fire side effects or delay user feedback.
  retryCondition: (e) => axiosRetry.isNetworkError(e),
})

/*** Token clear helpers */
export const clearTokens = () => {
  cookieService.clearCookies()
  // Theme is a device preference, not session data — keep it across logout / 401.
  // So is the step each Plan of Care draft was left on.
  clearLocalStorageExcept(['themeMode', POC_DRAFT_PROGRESS_KEY])
}

export const updateLastActivity = () => {
  saveToLocalStorage('lastActivity', Date.now().toString())
}

/***  Refresh-on-401 coordination
Single in-flight refresh shared across all concurrent 401s, so N parallel
failing requests trigger exactly one /refresh call.  */
let refreshInFlight: Promise<string> | null = null

const refreshAccessToken = (): Promise<string> => {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const rt = cookieService.getValue('refresh_token')
    const { data } = await axios.post(
      `${BASE_URL}${apiPath.REFRESH_TOKEN}?refreshToken=${rt}`,
      {},
      { timeout: 10000 },
    )
    const tokenData = data?.data
    writeAuthTokensToCookies(tokenData)
    return tokenData.access_token as string
  })().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

/*** Request interceptor */
axiosInstance.interceptors.request.use(async (req: InternalAxiosRequestConfig) => {
  globalThis.dispatchEvent(new Event('api-activity'))

  const skip = SKIP_URLS.some((u) => req.url?.includes(u))
  const token = cookieService.getValue('access_token')
  if (token && !skip) req.headers.Authorization = `Bearer ${token}`

  if (req.url === apiPath.LOGOUT_USER) {
    const rt = cookieService.getValue('refresh_token')
    if (rt) req.data = { ...req.data, refreshToken: rt }
  }

  return req
})

/**
 * Every backend response carries the version of the role → permission matrix. When it differs
 * from the last one seen, an admin has saved a change (or the server restarted): the signed-in
 * user's permission list is re-read once, so menus and buttons follow on their very next request
 * instead of waiting for a tab switch or a 403. No header, no version, nothing happens — and the
 * first value seen is only remembered, never acted on.
 */
const PERMISSIONS_VERSION_HEADER = 'x-permissions-version'
let permissionsVersion: string | undefined
const notePermissionsVersion = (headers: unknown) => {
  const version = (headers as Record<string, unknown> | undefined)?.[PERMISSIONS_VERSION_HEADER]
  if (typeof version !== 'string' || version === permissionsVersion) return
  const changed = permissionsVersion !== undefined
  permissionsVersion = version
  if (changed) queryClient.invalidateQueries({ queryKey: permissionQueryKeys.me() })
}

/*** Response interceptor */
axiosInstance.interceptors.response.use(
  (res) => {
    notePermissionsVersion(res.headers)
    if (res.config.url === apiPath.LOGIN && res.data?.data?.access_token) {
      const d = res.data.data
      writeAuthTokensToCookies(d)
      saveToLocalStorage('lastActivity', Date.now().toString())
    }

    return res.data
  },
  async (error) => {
    notePermissionsVersion(error?.response?.headers)
    const status = error?.response?.status
    const orig = error?.config
    if (status === 401 && !orig?._retry && orig?.url !== apiPath.LOGIN) {
      orig._retry = true
      try {
        const newAccessToken = await refreshAccessToken()
        orig.headers.Authorization = `Bearer ${newAccessToken}`
        return axiosInstance(orig)
      } catch (e) {
        console.warn('[AUTH] Refresh failed on 401 — clearing tokens and redirecting to login')
        clearTokens()
        globalThis.location.assign('/auth/login')
        throw e
      }
    }

    if (status === 403 && orig?.url !== apiPath.ME_PERMISSIONS) {
      // The caller's permissions may have been changed by an admin mid-session. Refresh the
      // set so menus and buttons correct themselves; the failed request is not retried. A 403
      // on the permissions call itself must not trigger another read of it.
      queryClient.invalidateQueries({ queryKey: permissionQueryKeys.me() })
    }

    return Promise.reject(error?.response || { data: { message: 'An error occurred' } })
  },
)
export default axiosInstance
